import { BrandingClient } from '@frontend/account/api';
import { AuthenticationManager, Token } from '@frontend/authentication-v2';
import { getCookie } from '@frontend/common';
import { ICONS } from '@frontend/icons';
import { Logger } from '@frontend/logger';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { LoadingScreen } from '../loading-screen/loading-screen';
import default_logo_colapsed from './logo200x200.png';

const default_logo_expanded = ICONS.CORPORATE.TELLOPORT;

const TELLOPORT_SELECTED_ACCOUNT_COOKIE = 'tp_prev_sel_acc';
const ACCOUNT_UPDATE_EVENT = `cookie:${TELLOPORT_SELECTED_ACCOUNT_COOKIE}`;
interface BrandingStore {
    domain: string | null;
    account_id: string | null;
    favicon: string | null;
    logo_account_expanded: string | null;
    logo_account_collapsed: string | null;
    logo_navbar_expanded: string;
    logo_navbar_collapsed: string;
    brand_primary: string | null;
    brand_secondary: string | null;
    brand_muted: string | null;
    brand_light: string | null;
    brand_disabled: string | null;
    status_danger: string | null;
    status_success: string | null;
    status_warning: string | null;
    status_info: string | null;
    foreground_default: string | null;
    background_surface: string | null;
    background_default: string | null;
    border_default: string | null;
    always_white: string | null;
    always_black: string | null;
}

const BrandingStoreContext = createContext<BrandingStore>({
    domain: null,
    account_id: null,
    favicon: null,
    logo_account_expanded: default_logo_expanded,
    logo_account_collapsed: default_logo_colapsed,
    logo_navbar_expanded: default_logo_expanded,
    logo_navbar_collapsed: default_logo_colapsed,
    brand_primary: null,
    brand_secondary: null,
    brand_muted: null,
    brand_light: null,
    brand_disabled: null,
    status_danger: null,
    status_success: null,
    status_warning: null,
    status_info: null,
    foreground_default: null,
    background_surface: null,
    background_default: null,
    border_default: null,
    always_white: null,
    always_black: null
});
export type BrandingStoreProviderProps = {
    children: React.ReactNode;
    defaultLogoExpanded?: string;
    defaultLogoCollapsed?: string;
};

export const BrandingStoreProvider = (props: BrandingStoreProviderProps) => {
    const logoExpanded = props.defaultLogoExpanded ?? default_logo_expanded;
    const logoCollapsed = props.defaultLogoCollapsed ?? default_logo_colapsed;
    const [isLoading, setIsLoading] = useState(true);
    const [expectedAccount, setExpectedAccount] = useState<string | null>(getCookie(TELLOPORT_SELECTED_ACCOUNT_COOKIE) ?? null);
    const [token, setToken] = useState<string | null>(null);
    const [store, setStore] = useState<BrandingStore>({
        domain: null,
        account_id: null,
        favicon: null,
        logo_account_expanded: logoExpanded,
        logo_account_collapsed: logoCollapsed,
        logo_navbar_expanded: logoExpanded,
        logo_navbar_collapsed: logoCollapsed,
        brand_primary: null,
        brand_secondary: null,
        brand_muted: null,
        brand_light: null,
        brand_disabled: null,
        status_danger: null,
        status_success: null,
        status_warning: null,
        status_info: null,
        foreground_default: null,
        background_surface: null,
        background_default: null,
        border_default: null,
        always_white: null,
        always_black: null
    });

    const listener = () => {
        setExpectedAccount(getCookie(TELLOPORT_SELECTED_ACCOUNT_COOKIE) ?? null);
    };

    useEffect(() => {
        setIsLoading(true);
        BrandingClient.fetchBrandingByDomain(window.location.hostname)
            .then((res) => {
                if (res) {
                    setStore({
                        domain: res.domain,
                        account_id: null,
                        favicon: res.favicon,
                        logo_account_expanded: res.logo_account_expanded ?? logoExpanded,
                        logo_account_collapsed: res.logo_account_collapsed ?? logoCollapsed,
                        logo_navbar_expanded: res.logo_navbar_expanded ?? logoExpanded,
                        logo_navbar_collapsed: res.logo_navbar_collapsed ?? logoCollapsed,
                        brand_primary: res.brand_primary,
                        brand_secondary: res.brand_secondary,
                        brand_muted: res.brand_muted,
                        brand_light: res.brand_light,
                        brand_disabled: res.brand_disabled,
                        status_danger: res.status_danger,
                        status_success: res.status_success,
                        status_warning: res.status_warning,
                        status_info: res.status_info,
                        foreground_default: res.foreground_default,
                        background_surface: res.background_surface,
                        background_default: res.background_default,
                        border_default: res.border_default,
                        always_white: res.always_white,
                        always_black: res.always_black
                    });
                }
            })
            .catch((error) => {
                Logger.warn('No domain branding found.');
            })
            .finally(() => setIsLoading(false));

        window.addEventListener(ACCOUNT_UPDATE_EVENT, listener);

        return () => {
            window.removeEventListener(ACCOUNT_UPDATE_EVENT, listener);
        };
    }, [window.location.hostname, logoExpanded, logoCollapsed]);

    useEffect(() => {
        if (expectedAccount && expectedAccount !== store?.account_id && AuthenticationManager.getInstance().hasValidToken()) {
            setIsLoading(true);
            BrandingClient.fetchBrandingByAccount(expectedAccount)
                .then((res) => {
                    if (res) {
                        setStore({
                            domain: res.domain,
                            account_id: expectedAccount,
                            favicon: res.favicon,
                            logo_account_expanded: res.logo_account_expanded ?? logoExpanded,
                            logo_account_collapsed: res.logo_account_collapsed ?? logoCollapsed,
                            logo_navbar_expanded: res.logo_navbar_expanded ?? logoExpanded,
                            logo_navbar_collapsed: res.logo_navbar_collapsed ?? logoCollapsed,
                            brand_primary: res.brand_primary,
                            brand_secondary: res.brand_secondary,
                            brand_muted: res.brand_muted,
                            brand_light: res.brand_light,
                            brand_disabled: res.brand_disabled,
                            status_danger: res.status_danger,
                            status_success: res.status_success,
                            status_warning: res.status_warning,
                            status_info: res.status_info,
                            foreground_default: res.foreground_default,
                            background_surface: res.background_surface,
                            background_default: res.background_default,
                            border_default: res.border_default,
                            always_white: res.always_white,
                            always_black: res.always_black
                        });
                    }
                })
                .catch((error) => {
                    Logger.warn('No account branding found: defaulting to domain branding');
                    const defaultTheme = getDefaultTheme();
                    setStore((prev) => ({
                        domain: null,
                        account_id: expectedAccount,
                        favicon: null,
                        logo_account_expanded: logoExpanded,
                        logo_account_collapsed: logoCollapsed,
                        logo_navbar_expanded: logoExpanded,
                        logo_navbar_collapsed: logoCollapsed,
                        brand_primary: defaultTheme['brand-primary'],
                        brand_secondary: defaultTheme['brand-secondary'],
                        brand_muted: defaultTheme['brand-muted'],
                        brand_light: defaultTheme['brand-light'],
                        brand_disabled: defaultTheme['brand-disabled'],
                        status_danger: defaultTheme['status-danger'],
                        status_success: defaultTheme['status-success'],
                        status_warning: defaultTheme['status-warning'],
                        status_info: defaultTheme['status-info'],
                        foreground_default: defaultTheme['foreground-default'],
                        background_surface: defaultTheme['background-surface'],
                        background_default: defaultTheme['background-default'],
                        border_default: defaultTheme['border-default'],
                        always_white: defaultTheme['always-white'],
                        always_black: defaultTheme['always-black']
                    }));
                })
                .finally(() => setIsLoading(false));
        }
    }, [expectedAccount, store?.account_id, token, logoExpanded, logoCollapsed]);

    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (!e || !e.newValue) return;
            const value = JSON.parse(e.newValue) as Token;
            setToken(value.jwt_token);
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    useEffect(() => {
        if (store) {
            const root = document.documentElement;
            const defaultTheme = getDefaultTheme();
            Object.keys(defaultTheme).forEach((key) => {
                root.style.setProperty(`--color-${key}`, (defaultTheme as { [key: string]: string })[key]);
            });

            const hostPrimary = (defaultTheme as { [key: string]: string })['brand-primary'];
            if (store.brand_primary) {
                root.style.setProperty('--color-brand-primary', store.brand_primary);
                // White-label API primary also drives accent; Telloport navy keeps host teal accent.
                if (store.brand_primary.toLowerCase() !== hostPrimary.toLowerCase()) {
                    root.style.setProperty('--color-brand-accent', store.brand_primary);
                }
            }
            store.brand_secondary && root.style.setProperty('--color-brand-secondary', store.brand_secondary);
            // Structure chrome follows secondary when account branding supplies it; else host default.
            root.style.setProperty('--color-brand-structure', store.brand_secondary ?? (defaultTheme as { [key: string]: string })['brand-structure']);
            store.brand_muted && root.style.setProperty('--color-brand-muted', store.brand_muted);
            store.brand_light && root.style.setProperty('--color-brand-light', store.brand_light);
            store.brand_disabled && root.style.setProperty('--color-brand-disabled', store.brand_disabled);
            store.status_danger && root.style.setProperty('--color-status-danger', store.status_danger);
            store.status_success && root.style.setProperty('--color-status-success', store.status_success);
            store.status_warning && root.style.setProperty('--color-status-warning', store.status_warning);
            store.status_info && root.style.setProperty('--color-status-info', store.status_info);
            store.foreground_default && root.style.setProperty('--color-foreground-default', store.foreground_default);
            store.background_surface && root.style.setProperty('--color-background-surface', store.background_surface);
            store.background_default && root.style.setProperty('--color-background-default', store.background_default);
            store.border_default && root.style.setProperty('--color-border-default', store.border_default);
            store.always_white && root.style.setProperty('--color-always-white', store.always_white);
            store.always_black && root.style.setProperty('--color-always-black', store.always_black);

            updateTitleFromManifestPath();
        }
    }, [store]);

    if (isLoading) return <LoadingScreen />;
    return <BrandingStoreContext.Provider value={store}>{props.children}</BrandingStoreContext.Provider>;
};

export const useBrandingRepository = () => {
    const context = useContext(BrandingStoreContext);
    if (!context) {
        throw new Error('useBrandingRepository must be used within an BrandingStoreProvider');
    }
    return context;
};

export const updateTitleFromManifestPath = async (manifestPath: string = '/public/manifest.json'): Promise<void> => {
    try {
        const response = await fetch(manifestPath);

        if (!response.ok) {
            return;
        }

        const manifest: {
            short_name?: string;
            name?: string;
        } = await response.json();

        if (manifest.short_name) {
            document.title = manifest.short_name;
        } else if (manifest.name) {
            document.title = manifest.name;
            Logger.warn('short_name not found in manifest, using name instead');
        } else {
            Logger.warn('Neither short_name nor name found in manifest.json');
        }
    } catch (error) {
        Logger.error('Error updating title from manifest:', error);
    }
};

const DEFAULT_BACKGROUND_DEFAULT = '#F5F7F8';
const KIOSK_BACKGROUND_DEFAULT = '#D4D4D4';

/** Telloport Design System defaults (not used for bpost / S4M host overrides). */
export const TELLOPORT_DEFAULT_THEME = {
    /** Navy — CTAs, links, and primary actions. */
    'brand-primary': '#1C3A4A',
    /** Darker navy — hover / pressed on primary. */
    'brand-secondary': '#0F2430',
    /** Navy chrome / heroes (same as primary). */
    'brand-structure': '#1C3A4A',
    'brand-muted': '#49616E',
    /** Teal-t80 wash — soft chips / status backgrounds (DS teal scale). */
    'brand-light': '#DCF1EF',
    /** Telloport teal — active rails, tab indicators, focus accents. */
    'brand-accent': '#4FB8B0',
    /** Light wash — disabled fills / borders (not disabled label text; use brand-muted). */
    'brand-disabled': '#C5D0D3',
    'status-danger': '#EF2637',
    'status-success': '#009B1A',
    'status-warning': '#DD7600',
    'status-info': '#1C3A4A',
    'foreground-default': '#0F1E26',
    'background-surface': '#FFFFFF',
    'border-default': '#CED9DB',
    'always-white': '#FFFFFF',
    'always-black': '#0E1D25'
} as const;

type ThemeHostLocation = Pick<Location, 'hostname' | 'href'>;

/** Host-aware Telloport / white-label defaults (bpost + S4M branches stay independent of DS remaps). */
export const getDefaultTheme = (locationLike: ThemeHostLocation = window.location) => {
    const isKiosk = locationLike.href.includes('kiosk.telloport');
    const backgroundDefault = isKiosk ? KIOSK_BACKGROUND_DEFAULT : DEFAULT_BACKGROUND_DEFAULT;

    if (locationLike.hostname.includes('solutions4materials'))
        return {
            'brand-primary': '#FDB913',
            'brand-secondary': '#D69800',
            'brand-structure': '#101010',
            'brand-muted': '#939598',
            'brand-light': '#4D3700',
            'brand-accent': '#FDB913',
            'brand-disabled': '#999999',
            'status-danger': '#A60916',
            'status-success': '#009B1A',
            'status-warning': '#DD7600',
            'status-info': '#A3A279',
            'foreground-default': '#FFFFFF',
            'background-surface': '#101010',
            'background-default': '#1F1F1F',
            'border-default': '#333333',
            'always-white': '#FFFFFF',
            'always-black': '#101010'
        };

    if (locationLike.hostname.includes('bpost'))
        return {
            'brand-primary': '#EF2637',
            'brand-secondary': '#A60916',
            'brand-structure': '#A60916',
            'brand-muted': '#434F56',
            'brand-light': '#F9ECEC',
            'brand-accent': '#EF2637',
            'brand-disabled': '#999999',
            'status-danger': '#A85A00',
            'status-success': '#009B1A',
            'status-warning': '#DD7600',
            'status-info': '#A60916',
            'foreground-default': '#101010',
            'background-surface': '#FFFFFF',
            'background-default': '#F0F0F0',
            'border-default': '#D9D9D9',
            'always-white': '#FFFFFF',
            'always-black': '#101010'
        };

    return {
        ...TELLOPORT_DEFAULT_THEME,
        'background-default': backgroundDefault
    };
};
